/**
 * PATH: packages/email-hub/src/feature/lib/orgRebind.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-ORG-REBIND-001
 * PURPOSE: One ordered RL-01 boundary for organisation changes.
 * WHY: React remounts reset hooks, not module caches/drafts/Zustand state.
 * FLOW: clear old cache -> publish new org/scope -> adopt provenanced drafts
 *       -> hydrate mailbox prefs -> layout-phase dock/AI cleanup.
 */

import {
  setEmailMailboxScope,
  setEmailTenantId,
} from '@quotech/tenant-shared/email/api'
import { useComposeDockStore } from '../compose/composeDockStore'
import { useAiCopilotStore } from '../store/aiCopilotStore'
import { rehydrateHiddenMailboxes } from '../stores/emailFilterStore'
import {
  resetEmailListCache,
  resetEmailSummaryMemoryCache,
} from '../hooks/useEmails.cache'
import { adoptDraftsForOrg } from './draftStore'

export interface EmailOrgRebindDeps {
  resetEmailSummaryMemoryCache: () => void
  resetEmailListCache: () => void
  setEmailTenantId: (orgId: number | null) => void
  setEmailMailboxScope: (scope: string | null) => void
  adoptDraftsForOrg: (orgId: number | null) => void
  rehydrateHiddenMailboxes: (orgId: number | null) => void
  closeComposeDock: () => void
  clearAiRequest: () => void
}

const runtimeDeps: EmailOrgRebindDeps = {
  resetEmailSummaryMemoryCache,
  resetEmailListCache,
  setEmailTenantId,
  setEmailMailboxScope,
  adoptDraftsForOrg,
  rehydrateHiddenMailboxes,
  closeComposeDock: () => useComposeDockStore.getState().closeAll(),
  clearAiRequest: () => useAiCopilotStore.getState().consume(),
}

/** Render-safe module/storage work. Caller invokes only on an actual switch. */
export function applyEmailOrgSwitchBoundary(
  nextOrgId: number | null,
  deps: EmailOrgRebindDeps = runtimeDeps,
): void {
  // Render phase: module writes only. No storage/network/Zustand notifications.
  deps.resetEmailSummaryMemoryCache()
  // Publish the new tenant and invalidate mailbox scope synchronously before
  // the org-keyed child tree mounts and fires its first request.
  deps.setEmailTenantId(nextOrgId)
  deps.setEmailMailboxScope(null)
}

/** Layout-phase external-store cleanup; runs before passive child effects. */
export function finishEmailOrgSwitchBoundary(
  nextOrgId: number | null,
  deps: EmailOrgRebindDeps = runtimeDeps,
): void {
  deps.resetEmailListCache()
  deps.adoptDraftsForOrg(nextOrgId)
  deps.rehydrateHiddenMailboxes(nextOrgId)
  deps.closeComposeDock()
  deps.clearAiRequest()
}

